import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate, STATUS_FLOW, STATUS_COLORS, PAY_COLORS } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Eye, FileText, Trash2, Archive, ArchiveRestore, X } from "lucide-react";

const GROUPS = {
  a_laver: ["Reçu"],
  en_cours: ["Lavage", "Séchage", "Repassage"],
  pretes: ["Prêt"],
  livrees: ["Livré"],
};
const TABS = [
  { key: "", label: "Toutes" },
  { key: "a_laver", label: "À laver" },
  { key: "en_cours", label: "En cours" },
  { key: "pretes", label: "Prêtes à livrer" },
  { key: "livrees", label: "Livrées" },
];

export default function Orders() {
  const nav = useNavigate();
  const { settings, isAdmin } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [list, setList] = useState([]);
  const [delId, setDelId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState(STATUS_FLOW[0]);
  const [applying, setApplying] = useState(false);
  const cur = settings?.currency || "KMF";

  // Filters live in the URL so they survive navigation (back from an order
  // detail returns to the exact same filtered view).
  const status = sp.get("status") || "";
  const group = sp.get("group") || "";
  const pay = sp.get("pay") || "";
  const dateFrom = sp.get("from") || "";
  const dateTo = sp.get("to") || "";
  const q = sp.get("q") || "";
  const showArchived = sp.get("arch") === "1";
  const page = Math.max(1, Number(sp.get("page") || "1"));

  // Status filtering is done client-side so quick-filter tabs can span several
  // statuses (e.g. "En cours" = Lavage + Séchage + Repassage).
  const activeStatuses = group && GROUPS[group] ? GROUPS[group] : (status ? [status] : null);

  const setParams = useCallback((patch, resetPage = true) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null || v === false) next.delete(k);
      else next.set(k, v === true ? "1" : String(v));
    });
    if (resetPage) next.delete("page");
    setSp(next, { replace: true });
  }, [sp, setSp]);

  const load = useCallback(async () => {
    const params = {};
    if (pay) params.payment_status = pay;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (showArchived) params.show_archived = true;
    try {
      const { data } = await api.get("/orders", { params });
      setList(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [pay, dateFrom, dateTo, showArchived]);

  useEffect(() => { load(); }, [load]);

  // Clear the multi-selection whenever the filter set changes.
  useEffect(() => { setSelected(new Set()); }, [status, group, pay, dateFrom, dateTo, showArchived]);

  const filtered = list.filter((o) => {
    if (activeStatuses && !activeStatuses.includes(o.status)) return false;
    return !q || o.order_number.toLowerCase().includes(q.toLowerCase()) ||
      o.customer_name.toLowerCase().includes(q.toLowerCase()) ||
      (o.customer_phone || "").includes(q);
  });

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const allPageSelected = paged.length > 0 && paged.every((o) => selected.has(o.id));
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) paged.forEach((o) => next.delete(o.id));
      else paged.forEach((o) => next.add(o.id));
      return next;
    });
  };

  const applyBulk = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setApplying(true);
    try {
      const { data } = await api.post("/orders/bulk-status", { order_ids: ids, status: bulkStatus });
      toast.success(`${data.updated} commande(s) → ${bulkStatus}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/orders/${delId}`);
      toast.success("Commande supprimée");
      setDelId(null);
      load();
    } catch (e) { toast.error(apiError(e)); setDelId(null); }
  };

  const toggleArchive = async (o) => {
    try {
      await api.put(`/orders/${o.id}/archive`, { archived: !o.archived });
      toast.success(o.archived ? "Commande désarchivée" : "Commande archivée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const selCls = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Commandes</h1>
        <Button data-testid="new-order-btn" onClick={() => nav("/orders/new")} className="bg-sky-600 hover:bg-sky-700">
          <Plus size={18} className="mr-2" /> Nouvelle commande
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="quick-filter-tabs">
        {TABS.map((t) => {
          const active = t.key === group || (t.key === "" && !group);
          return (
            <button
              key={t.key || "all"}
              data-testid={`quick-tab-${t.key || "all"}`}
              onClick={() => setParams({ group: t.key, status: "" })}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                active ? "bg-sky-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <Card className="p-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <Input data-testid="order-search" className="pl-9" placeholder="N° commande, client..." value={q} onChange={(e) => setParams({ q: e.target.value })} />
        </div>
        <select data-testid="filter-status" className={selCls} value={status} onChange={(e) => setParams({ status: e.target.value, group: "" })}>
          <option value="">Tous statuts</option>
          {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-payment" className={selCls} value={pay} onChange={(e) => setParams({ pay: e.target.value })}>
          <option value="">Tous paiements</option>
          {["Payé", "Partiel", "Non payé"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" data-testid="filter-date-from" className={selCls} value={dateFrom} onChange={(e) => setParams({ from: e.target.value })} />
        <input type="date" data-testid="filter-date-to" className={selCls} value={dateTo} onChange={(e) => setParams({ to: e.target.value })} />
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none px-1">
            <input type="checkbox" data-testid="toggle-show-archived" checked={showArchived} onChange={(e) => setParams({ arch: e.target.checked })} className="h-4 w-4 accent-sky-600" />
            Afficher les archivées
          </label>
        )}
      </Card>

      {selected.size > 0 && (
        <Card className="p-3 flex flex-wrap items-center gap-3 border-sky-300 bg-sky-50" data-testid="bulk-toolbar">
          <span className="text-sm font-semibold text-sky-800" data-testid="bulk-count">{selected.size} sélectionnée(s)</span>
          <span className="text-sm text-slate-600">Changer le statut en :</span>
          <select data-testid="bulk-status-select" className={selCls} value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
            {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button data-testid="bulk-apply-btn" onClick={applyBulk} disabled={applying} className="bg-sky-600 hover:bg-sky-700">
            {applying ? "Application..." : "Appliquer"}
          </Button>
          <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <X size={14} /> Annuler la sélection
          </button>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="data-table w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" data-testid="select-all-page" checked={allPageSelected} onChange={togglePage} className="h-4 w-4 accent-sky-600 align-middle" />
              </th>
              <th className="px-4 py-3">N°</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3 num">Total</th>
              <th className="px-4 py-3 num">Reste</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Paiement</th>
              <th className="px-4 py-3 hidden md:table-cell">Agent</th>
              <th className="px-4 py-3 hidden md:table-cell">Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} className={`border-t border-slate-100 hover:bg-slate-50 ${selected.has(o.id) ? "bg-sky-50" : ""}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" data-testid={`select-order-${o.id}`} checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} className="h-4 w-4 accent-sky-600 align-middle" />
                </td>
                <td className="px-4 py-3 font-medium text-slate-800">
                  {o.order_number}
                  {o.archived && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 align-middle">Archivée</span>}
                </td>
                <td className="px-4 py-3">{o.customer_name}</td>
                <td className="px-4 py-3 num font-medium">{fmtMoney(o.total, cur)}</td>
                <td className="px-4 py-3 num text-rose-600">{fmtMoney(o.remaining, cur)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${PAY_COLORS[o.payment_status]}`}>{o.payment_status}</span></td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{o.agent_name}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" data-testid={`receipt-order-${o.id}`} title="Reçu" onClick={() => nav(`/orders/${o.id}/receipt`)}><FileText size={16} /></Button>
                  <Button size="icon" variant="ghost" data-testid={`view-order-${o.id}`} title="Détails" onClick={() => nav(`/orders/${o.id}`)}><Eye size={16} /></Button>
                  {isAdmin && <Button size="icon" variant="ghost" className="text-amber-600" data-testid={`archive-order-${o.id}`} title={o.archived ? "Désarchiver" : "Archiver"} onClick={() => toggleArchive(o)}>{o.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}</Button>}
                  {isAdmin && <Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-order-${o.id}`} title="Supprimer" onClick={() => setDelId(o.id)}><Trash2 size={16} /></Button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Aucune commande</td></tr>}
          </tbody>
        </table>
      </Card>

      <Pagination page={page} total={filtered.length} onPage={(p) => setParams({ page: p === 1 ? "" : p }, false)} />

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette commande ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible et retirera la commande des rapports.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-order" onClick={remove} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
